import { useState, type ReactNode } from 'react';
import { Check, Pencil, Sparkles } from 'lucide-react';
import type { EvaluationBlock } from '@interactive-room';

import { validateExamBlock, type ExamBlockExamContext } from '../../studio/components/ExamBlockInlineEditor';
import { BlockAiGenerateDialog } from '../../studio/components/BlockAiGenerateDialog';
import { supportsBlockAi } from '../../studio/constants/blockAiParams';
import { PrintInlineEditProvider } from '../../studio/print/PrintInlineEditContext';

export interface EditableBlockWrapperProps {
  children: ReactNode;
  /** When false, renders children only (print-only flows). */
  enabled?: boolean;
  blockLabel?: string;
  block: EvaluationBlock;
  examContext?: ExamBlockExamContext;
  onChange?: (block: EvaluationBlock) => void;
}

/**
 * Modo edición WYSIWYG: al pulsar Editar, los textos del bloque se editan
 * directamente en la hoja (sin formulario). IA sigue como diálogo.
 */
export function EditableBlockWrapper({
  children,
  enabled = true,
  blockLabel,
  block,
  examContext,
  onChange,
}: EditableBlockWrapperProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  if (!enabled) {
    return <>{children}</>;
  }

  const editTarget = blockLabel?.trim() || 'bloque';
  const canAi =
    Boolean(examContext) &&
    Boolean(onChange) &&
    supportsBlockAi(block.mode, examContext!.resourceType, examContext!.subjectTag);

  return (
    <PrintInlineEditProvider isEditing={isEditing} block={block} onChange={onChange}>
      <div
        className={[
          'print-block-edit group relative rounded-xl transition-all duration-200',
          isEditing ? 'print-block-edit--open print-block-edit--inline' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="print-block-edit__actions no-print">
          {isEditing ? (
            <button
              type="button"
              className="print-block-edit__btn print-block-edit__btn--done"
              aria-label={`Listo con ${editTarget}`}
              onClick={() => setIsEditing(false)}
            >
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Listo
            </button>
          ) : (
            <>
              {canAi ? (
                <button
                  type="button"
                  className="print-block-edit__btn print-block-edit__btn--ai"
                  aria-label={`Mejorar con IA ${editTarget}`}
                  onClick={() => setAiOpen(true)}
                >
                  <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  IA
                </button>
              ) : null}
              <button
                type="button"
                className="print-block-edit__btn"
                aria-label={`Editar ${editTarget}`}
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Editar
              </button>
            </>
          )}
        </div>

        {canAi && examContext && onChange ? (
          <BlockAiGenerateDialog
            open={aiOpen}
            onOpenChange={setAiOpen}
            block={block}
            examContext={examContext}
            onGenerated={(next) => {
              onChange(validateExamBlock(next));
              setIsEditing(true);
            }}
          />
        ) : null}

        {children}
      </div>
    </PrintInlineEditProvider>
  );
}
